"use client";

import {
  useCallback,
  type Dispatch,
  type RefObject,
  type SetStateAction,
} from "react";
import type {
  ScrollHintState,
  TimelineViewportState,
} from "./timeline-root-types";
import {
  EMPTY_SCROLL_HINT_STATE,
  EMPTY_VIEWPORT_STATE,
  areScrollHintStatesEqual,
  areTimelineViewportStatesEqual,
  selectCenteredPeriodIndexFromNodes,
  selectScrollHintStateFromNode,
  selectTimelineViewportStateFromNode,
} from "./timeline-scroll-selectors";

export interface UseTimelineScrollUiUpdaterInput {
  innerRef: RefObject<HTMLDivElement | null>;
  scrollContainerRef: RefObject<HTMLDivElement | null>;
  setCenteredPeriodIndex: Dispatch<SetStateAction<number>>;
  setScrollHintState: Dispatch<SetStateAction<ScrollHintState>>;
  setViewportState: Dispatch<SetStateAction<TimelineViewportState>>;
}

export function useTimelineScrollUiUpdater({
  innerRef,
  scrollContainerRef,
  setCenteredPeriodIndex,
  setScrollHintState,
  setViewportState,
}: UseTimelineScrollUiUpdaterInput): () => void {
  return useCallback((): void => {
    const scrollContainerNode = scrollContainerRef.current;
    if (!scrollContainerNode) {
      setScrollHintState(EMPTY_SCROLL_HINT_STATE);
      setViewportState(EMPTY_VIEWPORT_STATE);
      return;
    }

    const nextScrollHintState = selectScrollHintStateFromNode({
      scrollContainerNode,
    });
    setScrollHintState((previous) =>
      areScrollHintStatesEqual({ previous, nextState: nextScrollHintState })
        ? previous
        : nextScrollHintState,
    );

    const nextViewportState = selectTimelineViewportStateFromNode({
      scrollContainerNode,
    });
    setViewportState((previous) =>
      areTimelineViewportStatesEqual({ previous, nextState: nextViewportState })
        ? previous
        : nextViewportState,
    );

    const innerNode = innerRef.current;
    if (!innerNode) {
      return;
    }

    const nextCenteredIndex = selectCenteredPeriodIndexFromNodes({
      innerNode,
      scrollContainerNode,
    });
    setCenteredPeriodIndex((previous) =>
      previous === nextCenteredIndex ? previous : nextCenteredIndex,
    );
  }, [
    innerRef,
    scrollContainerRef,
    setCenteredPeriodIndex,
    setScrollHintState,
    setViewportState,
  ]);
}
